import React, { useEffect } from 'react';
import { Mountain, ArrowRight, BookOpen, Cloud, Building2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { blogPosts } from '../../data/blogPosts';

const categoryMeta: Record<string, { icon: React.FC<{ className?: string }>; color: string; bg: string }> = {
  'Roam Weather': {
    icon: Cloud,
    color: 'text-blue-600',
    bg: 'bg-blue-100',
  },
  'Roam Office Tracker': {
    icon: Building2,
    color: 'text-orange-600',
    bg: 'bg-orange-100',
  },
};

export const BlogIndex: React.FC = () => {
  useEffect(() => {
    document.title = 'Blog | Roam — Outdoor Adventure Apps';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        'Tips, guides, and updates from the Roam team — covering weather planning for climbers and backpackers, and office day tracking for hybrid workers.'
      );
    }
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <Link to="/" className="flex items-center space-x-3">
              <div className="bg-gradient-to-br from-blue-500 to-purple-600 p-2 rounded-xl">
                <Mountain className="h-8 w-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Roam</h1>
                <p className="text-sm text-gray-600">Outdoor Adventure Apps</p>
              </div>
            </Link>

            <nav className="hidden md:flex items-center space-x-8">
              <Link to="/" className="text-gray-700 hover:text-blue-600 transition-colors">
                Home
              </Link>
              <Link to="/blog" className="text-blue-600 font-medium transition-colors">
                Blog
              </Link>
              <a
                href="https://weather.roamapps.com"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
              >
                Try Weather
              </a>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero */}
      <div className="bg-gradient-to-br from-blue-600 to-purple-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <div className="inline-flex items-center justify-center bg-white/20 p-3 rounded-full mb-6">
            <BookOpen className="h-8 w-8" />
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold mb-4">Roam Blog</h2>
          <p className="text-xl text-blue-100 max-w-2xl mx-auto">
            Tips, guides, and updates from the Roam team — helping you plan better adventures
            and navigate hybrid work with confidence.
          </p>
        </div>
      </div>

      {/* Posts Grid */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {blogPosts.map((post) => {
            const cat = categoryMeta[post.category];
            const Icon = cat?.icon;
            return (
              <article
                key={post.slug}
                className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 flex flex-col"
              >
                <div className="p-8 flex flex-col flex-1">
                  {/* Category badge */}
                  <div className="flex items-center gap-2 mb-4">
                    {Icon && (
                      <div className={`${cat.bg} p-1.5 rounded-lg`}>
                        <Icon className={`h-4 w-4 ${cat.color}`} />
                      </div>
                    )}
                    <span className={`text-sm font-medium ${cat?.color ?? 'text-gray-600'}`}>
                      {post.category}
                    </span>
                  </div>

                  <h2 className="text-xl font-bold text-gray-900 mb-3 leading-snug">
                    {post.title}
                  </h2>

                  <p className="text-gray-600 leading-relaxed mb-6 flex-1">{post.description}</p>

                  <div className="flex items-center justify-between mt-auto">
                    <span className="text-sm text-gray-400">
                      {post.date} · {post.readTime}
                    </span>
                    <Link
                      to={`/blog/${post.slug}`}
                      className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 font-medium text-sm transition-colors"
                    >
                      Read more
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm">© 2026 Roam. All rights reserved.</p>
            <div className="flex space-x-6 mt-4 md:mt-0">
              <Link to="/" className="text-gray-400 hover:text-white text-sm transition-colors">
                Home
              </Link>
              <Link to="/blog" className="text-gray-400 hover:text-white text-sm transition-colors">
                Blog
              </Link>
              <Link to="/support" className="text-gray-400 hover:text-white text-sm transition-colors">
                Support
              </Link>
              <Link to="/privacy" className="text-gray-400 hover:text-white text-sm transition-colors">
                Privacy Policy
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};
